import { useState } from "react";
import { BudgetBar, List, ListRow } from "@core/components/List";
import { Stepper } from "@core/components/Stepper";
import { Value } from "@core/components/Value";
import { AddItemSheet, InventoryRows, ItemEditor } from "./inventory";
import { page, resourceLabel, useSm, type InventoryView, type ItemOrigin } from "./view";

/** Step 11: the basic kit, the tools of the education, one weapon and purchases from the start money (GRW p. 26). */
export function EquipmentStep() {
  const { view, apply, budget } = useSm();
  const kit = view.startKit;
  const [kitChoices, setKitChoices] = useState<Record<string, number>>({});
  const [adding, setAdding] = useState<ItemOrigin | "relic" | null>(null);
  const [relicFor, setRelicFor] = useState<string | null>(null);
  const [editing, setEditing] = useState<InventoryView | null>(null);
  const kitComplete = kit.choices.every((c) => kitChoices[c.id] !== undefined);
  const of = (origin: ItemOrigin) => view.inventory.filter((i) => i.origin === origin);
  const tools = budget(view.budgets.tools);
  const startMoney = budget(view.budgets.startMoney);
  const spent = budget(view.budgets.moneySpent);
  const startWeapons = of("startWeapon");
  const other = of("received");
  const relicResources = view.resources.filter((r) => r.relicPoints);

  return (
    <div className="stack">
      <section className="card stack">
        <div className="row spread">
          <h3>1. Grundausstattung für eine Reise</h3>
          <span className="source">{page(kit.source)}</span>
        </div>
        <p className="muted small">Bekommt jeder Abenteurer kostenlos: {kit.items.join(", ")}, dazu Kleidung und Licht nach Wahl.</p>
        {kit.added ? (
          <InventoryRows items={of("basicKit")} onEdit={setEditing} />
        ) : (
          <>
            {kit.choices.map((choice) => (
              <label key={choice.id} className="field">
                <span>{choice.label}</span>
                <select value={kitChoices[choice.id] ?? ""} onChange={(e) => setKitChoices({ ...kitChoices, [choice.id]: Number(e.target.value) })}>
                  <option value="" disabled>
                    Bitte wählen …
                  </option>
                  {choice.options.map((option, index) => (
                    <option key={index} value={index}>
                      {option}
                    </option>
                  ))}
                </select>
                {choice.note && <span className="muted small">{choice.note}</span>}
              </label>
            ))}
            <div className="actions">
              <button type="button" className="button primary" disabled={!kitComplete} onClick={() => void apply({ type: "sm.addStartKit", payload: { choices: kitChoices } })}>
                Grundausstattung übernehmen
              </button>
            </div>
          </>
        )}
      </section>

      <section className="card stack">
        <h3>2. Handwerkszeug der Ausbildung</h3>
        <p className="muted small">
          Was der Abenteurer für sein Handwerk braucht, bekommt er bis zu einem Wert von {kit.toolsMaxText}: ein Alchemist den Alchemistenkoffer, ein Einbrecher Dietriche, Seil und
          Wurfhaken, ein Kämpfer Waffe und Rüstung. Was passt, entscheidet der Spielleiter.
        </p>
        <BudgetBar label="Handwerkszeug" used={tools} total={kit.toolsMax} usedText={<Value valueKey={view.budgets.tools} />} totalText={kit.toolsMaxText} />
        <InventoryRows items={of("tools")} onEdit={setEditing} empty="Noch kein Handwerkszeug gewählt." />
        <div className="actions">
          <button type="button" className="button secondary" onClick={() => setAdding("tools")}>
            + Handwerkszeug hinzufügen
          </button>
        </div>
      </section>

      <section className="card stack">
        <h3>3. Eine Waffe</h3>
        <p className="muted small">
          Zusätzlich darf jeder Abenteurer eine Waffe wählen, die etwa {kit.weaponMaxText} wert sein sollte; auch dann, wenn schon eine Waffe zum Handwerkszeug gehört.
        </p>
        <InventoryRows items={startWeapons} onEdit={setEditing} empty="Noch keine Startwaffe gewählt." />
        <div className="actions">
          <button type="button" className="button secondary" onClick={() => setAdding("startWeapon")}>
            {startWeapons.length === 0 ? "+ Waffe wählen" : "+ Weitere Waffe"}
          </button>
        </div>
      </section>

      <section className="card stack">
        <h3>4. Einkäufe vom Startvermögen</h3>
        <p className="muted small">
          Das Startvermögen hängt von der Ressource Vermögen ab und steht zur freien Verfügung. Was davon übrig bleibt, nimmt der Abenteurer mit ins Spiel.
        </p>
        <BudgetBar
          label="Startvermögen"
          used={spent}
          total={startMoney}
          usedText={<Value valueKey={view.budgets.moneySpent} />}
          totalText={<Value valueKey={view.budgets.startMoney} />}
          hint={
            <>
              Übrig: <Value valueKey={view.budgets.money} />
            </>
          }
        />
        <InventoryRows items={of("purchased")} onEdit={setEditing} empty="Noch nichts gekauft." />
        <div className="actions">
          <button type="button" className="button secondary" onClick={() => setAdding("purchased")}>
            + Kaufen
          </button>
        </div>
      </section>

      <section className="card stack">
        <h3>5. Relikte und Erhaltenes</h3>
        {relicResources.length > 0 ? (
          <>
            <p className="small">
              Die Ressource Relikt gibt dir je Instanz einen Gegenstand kostenlos: Wähle ihn mit „+ Relikt“ oder ordne einen vorhandenen im Gegenstand zu. Jeder Punkt bezahlt eine
              Stufe Qualität oder Artefaktqualität; die Qualität verteilst du im Gegenstand auf Verbesserungen (GRW S. 79, 142).
            </p>
            {relicResources.map((r) =>
              r.relicPoints ? (
                <div key={r.key} className="stack small-gap">
                  <BudgetBar
                    label={`Reliktpunkte ${resourceLabel(view, r.key)}`}
                    used={budget(r.relicPoints.spent)}
                    total={budget(r.relicPoints.total)}
                    usedText={<Value valueKey={r.relicPoints.spent} />}
                    totalText={<Value valueKey={r.relicPoints.total} />}
                  />
                  <div className="row spread">
                    <span className="small">
                      Zuschlag der Spielleitung <span className="muted">für ein großes oder mächtiges Relikt, geht von den Reliktpunkten ab (GRW S. 79)</span>
                    </span>
                    <Stepper
                      label={`Zuschlag ${resourceLabel(view, r.key)}`}
                      value={r.relicSurcharge ?? 0}
                      min={0}
                      onChange={(points) => apply({ type: "sm.setRelicSurcharge", payload: { instance: r.key, points } })}
                    />
                  </div>
                </div>
              ) : null,
            )}
          </>
        ) : (
          <p className="muted small">Ein Relikt gehört zur Ressource Relikt und kostet kein Geld (GRW S. 79); Geschenke und Erbstücke ebenso wenig. Herkunft und Relikt lassen sich an jedem Gegenstand ändern.</p>
        )}
        <InventoryRows items={other} onEdit={setEditing} empty="Nichts dergleichen." />
        <div className="actions">
          {relicResources.map((r) => (
            <button
              key={r.key}
              type="button"
              className="button secondary"
              onClick={() => {
                setRelicFor(r.key);
                setAdding("relic");
              }}
            >
              + {resourceLabel(view, r.key)}
            </button>
          ))}
          <button type="button" className="button secondary" onClick={() => setAdding("received")}>
            + Erhaltenes
          </button>
        </div>
      </section>

      <section className="card">
        <List aria-label="Zusammenfassung">
          <ListRow title="Gegenstände insgesamt" value={view.inventory.reduce((a, i) => a + i.quantity, 0)} />
          <ListRow title="Gesamtwert" value={<Value valueKey={view.inventoryTotals.value} />} />
          <ListRow title="Gesamtlast" value={<Value valueKey={view.inventoryTotals.load} />} />
          {view.carrying && (
            <ListRow
              title="Getragene Last ohne Grundausstattung"
              value={
                <>
                  <Value valueKey={view.carrying.carried} /> von <Value valueKey={view.carrying.capacity} />
                </>
              }
            />
          )}
          <ListRow title="Startvermögen übrig" value={<Value valueKey={view.budgets.money} />} />
        </List>
      </section>

      {adding && (
        <AddItemSheet
          title={{ tools: "Handwerkszeug hinzufügen", startWeapon: "Startwaffe wählen", purchased: "Kaufen", relic: "Relikt hinzufügen", received: "Erhaltenes hinzufügen", basicKit: "Hinzufügen", crafted: "Herstellen" }[adding]}
          origin={adding === "relic" ? "received" : adding}
          relic={adding === "relic" ? (relicFor ?? undefined) : undefined}
          kinds={adding === "startWeapon" ? ["weapon"] : undefined}
          onClose={() => setAdding(null)}
        />
      )}
      {editing && <ItemEditor item={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
